import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { UserProfile, PublicUserProfile } from '../types/user';
import { updateUserProfile } from './userService';

/**
 * Checks if a user profile has all required fields completed
 * Required fields: photoURL, phoneNumber, pronouns, dateOfBirth
 */
export function isPublicProfileComplete(profile: PublicUserProfile | null): boolean {
    if (!profile) return false;

    const requiredFields = [
        profile.profile.displayName?.trim(),
        profile.profile.photoURL?.trim(),
        profile.profile.bio?.trim(),
    ];

    return requiredFields.every(field => field && field.length > 0);
}

export async function updateProfileCompletionStatus(
    uid: string,
): Promise<void> {
    const profile = await getPublicUserProfile(uid);
    const completed = isPublicProfileComplete(profile);
    updateUserProfile(uid, {
        metadata: {
            profileCompleted: completed,
        },
    });
}

export async function getPublicUserProfile(
    userId: string
): Promise<PublicUserProfile | null> {
    const profileRef = doc(db, 'publicUserProfiles', userId);
    const profileSnap = await getDoc(profileRef);

    if (!profileSnap.exists()) {
        return null;
    }

    const data = profileSnap.data();

    const profile: PublicUserProfile = {
        uid: data.uid,
        username: data.username || null,
        profile: {
            displayName: data.profile.displayName || null,
            photoURL: data.profile.photoURL || null,
            bio: data.profile.bio || null,
            location: data.profile.location || null,
        },
        preferences: {
            privacy: {
                profilePublic: data.preferences?.privacy?.profilePublic ?? true,
            },
        },
        metadata: {
            createdAt: data.metadata.createdAt?.toDate?.() || data.metadata.createdAt,
            updatedAt: data.metadata.updatedAt?.toDate?.() || data.metadata.updatedAt,
            lastLoginAt: data.metadata.lastLoginAt?.toDate?.() || data.metadata.lastLoginAt || null,
            accountStatus: data.metadata.accountStatus || 'active',
            emailVerified: data.metadata.emailVerified ?? false,
            profileCompleted: data.metadata.profileCompleted ?? false,
        },
    };

    return profile;
}

/**
 * Creates a public profile from a full user profile
 * Only includes fields that are meant to be publicly visible
 */
export function createPublicProfile(profile: UserProfile): PublicUserProfile {
    return {
        uid: profile.uid,
        profile: {
            displayName: profile.profile.displayName || null,
            photoURL: profile.profile.photoURL || null,
            bio: profile.profile.bio || null,
            location: profile.profile.location || null,
        },
        metadata: {
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            lastLoginAt: serverTimestamp(),
            accountStatus: profile.metadata.accountStatus || 'active',
            emailVerified: profile.metadata.emailVerified ?? false,
            profileCompleted: profile.metadata.profileCompleted ?? false,
        },
    };
}

// Update public user profile based on privacy settings
export async function updatePublicProfile(
    uid: string,
    profile: UserProfile
): Promise<void> {
    // Only create/update public profile if user wants their profile to be public
    if (!profile.preferences.privacy?.profilePublic) {
        // If profile should not be public, set minimal data or delete
        const privateProfileData = {
            uid,
            username: null,
            profile: {
                displayName: null,
                photoURL: null,
                bio: null,
                location: null,
            },
            preferences: {
                privacy: {
                    profilePublic: false,
                },
            },
            metadata: {
                createdAt: profile.metadata.createdAt,
                updatedAt: serverTimestamp(),
                lastLoginAt: profile.metadata.lastLoginAt || null,
                accountStatus: 'active' as const,
                emailVerified: profile.metadata.emailVerified ?? false,
                profileCompleted: profile.metadata.profileCompleted ?? false,
            },
        };
        await setDoc(doc(db, 'publicUserProfiles', uid), privateProfileData);
        return;
    }

    // Build public profile based on privacy settings
    const publicProfileData = {
        uid,
        // Username is initially null and will be set when user reserves a username
        // through the username reservation workflow (create /usernames/{username} doc)
        username: null,
        profile: {
            // displayName, photoURL, and bio are always included if profile is public
            displayName: profile.profile.displayName || null,
            photoURL: profile.profile.photoURL || null,
            bio: profile.profile.bio || null,
            // location is only included if user allows it
            location: profile.preferences.privacy?.showLocation
                ? (profile.profile.location || null)
                : null,
        },
        preferences: {
            privacy: {
                profilePublic: true,
            },
        },
        metadata: {
            createdAt: profile.metadata.createdAt,
            updatedAt: serverTimestamp(),
            lastLoginAt: profile.metadata.lastLoginAt || null,
            accountStatus: profile.metadata.accountStatus || ('active' as const),
            emailVerified: profile.metadata.emailVerified ?? false,
            profileCompleted: profile.metadata.profileCompleted ?? false,
        },
    };

    await setDoc(doc(db, 'publicUserProfiles', uid), publicProfileData);
}

/**
 * Gets the public profile for a user
 */
export async function getPublicProfile(
    userId: string
): Promise<PublicUserProfile | null> {
    const publicProfileRef = doc(db, 'publicProfiles', userId);
    const publicProfileSnap = await getDoc(publicProfileRef);

    if (!publicProfileSnap.exists()) {
        return null;
    }

    return publicProfileSnap.data() as PublicUserProfile;
}

/**
 * Get multiple public user profiles by their user IDs
 * Returns a map of userId -> PublicUserProfile
 */
export async function getPublicUserProfiles(userIds: string[]): Promise<Map<string, PublicUserProfile>> {
    const profiles = new Map<string, PublicUserProfile>();

    // Fetch all public user profiles in parallel
    const promises = userIds.map(async (userId) => {
        const profile = await getPublicUserProfile(userId);
        if (profile) {
            profiles.set(userId, profile);
        }
    });

    await Promise.all(promises);

    return profiles;
}
// Create user profile in Firestore with retry logic

